import React from 'react';
import { NavLink } from 'react-router-dom';
import { PolIcon } from 'components/PolIcon/policon';
import BadgeMercure from 'v4/features/front/navigation/components/BadgeMercure/BadgeMercure';
import { useNavigation } from "v4/features/front/navigation/contexts/NavigationContext";
import { useFrontContext } from "v4/contexts/FrontContext";
import { resolveRouteComponent } from "v4/features/front/navigation/utils/navigation.mapper";
import { isMatching } from "v4/features/front/navigation/utils/navigation.utils";

/**
 * MainHeaderNavigationItem - Gère l'affichage d'un item du menu principal (avec support sous-menus).
 */
export default function MainHeaderNavigationItem({ item }) {
    const { currentRoute } = useNavigation();
    const { activeListingRoute } = useFrontContext();
    const { route, isDetail } = currentRoute;

    // --- Helper : Vérifier l'état actif (récursif pour les sous-menus) ---
    const checkIsActive = (navItem) => {
        // En mode détail, on compare prioritairement avec le listing parent (pour le surlignage du menu)
        // sinon avec la route actuelle.
        const reference = isDetail ? (activeListingRoute || route) : route;

        if (isMatching(navItem, reference)) return true;

        // Récursion pour les sous-menus
        return navItem.children?.some(child => checkIsActive(child)) ?? false;
    };

    const isActive = checkIsActive(item);
    const hasChildren = item.children && item.children.length > 0;
    const itemConfig = resolveRouteComponent(item);
    const className = itemConfig?.className ?? item.slug ?? '';

    /**
     * Helper pour formater l'URL de destination
     */
    const getUrl = (url) => url ? (url.startsWith('/') ? url : `/${url}`) : '';

    return (
        <li className={`nav__list__item ${hasChildren ? 'nav__list__item--has-children' : ''} ${isActive ? 'active' : ''}`}>

            {/* Lien Principal */}
            {(item.url && itemConfig) ? (
                <NavLink
                    to={getUrl(item.url)}
                    className={`nav__list__item__link ${className}`}
                    activeClassName="active"
                    exact={item.url === '/'}
                    isActive={() => isActive}
                >
                    {item.icon && <PolIcon icon={item.icon} />}
                    <span>{item.label}</span>
                    <Badge item={item} />
                </NavLink>
            ) : (
                <span className={`nav__list__item__link ${className} ${isActive ? 'active' : ''}`} style={{lineHeight: '1'}}>
                    <span className="d-inline-flex align-items-center" style={{gap: '0.3rem'}}>
                        {item.icon && <PolIcon icon={item.icon} />}
                        <span>{item.label}</span>
                    </span>
                    <Badge item={item} />
                </span>
            )}

            {/* Sous-Menu (si applicable) */}
            {hasChildren && (
                <ul className="sub-menu">
                    {item.children.map(child => {
                        const childClassName = child.slug ?? '';
                        const isChildActive = isMatching(child, isDetail ? activeListingRoute : route);
                        
                        return (
                            <li key={child.native_identifier ?? child.url} className={`sub-menu__item ${isChildActive ? 'active' : ''}`}>
                                <NavLink
                                    to={getUrl(child.url)}
                                    className={`sub-menu__item__link ${childClassName}`}
                                    activeClassName="active"
                                    isActive={() => isChildActive}
                                >
                    <span className="d-inline-flex align-items-center" style={{gap: '0.3rem'}}>
                                        {child.icon && <PolIcon icon={child.icon}/>}
                                        <span>{child.label}</span>
                                    </span>
                                    <Badge item={child} />
                                </NavLink>
                            </li>
                        );
                    })}
                </ul>
            )}
        </li>
    );
}

/**
 * Sous-composant pour l'affichage conditionnel des badges Mercure
 */
function Badge({ item }) {
    const badgeId = item.metadata?.badge_id || item.badge_id || item.badgeId;
    if (!badgeId) return null;
    return <BadgeMercure badgeId={badgeId} />;
}
